"use client";

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useRouter } from 'next/navigation';
import { SidebarNav } from '@/components/layout/sidebar';
import { FileBrowser, type ShareBrowseContext } from '@/components/browser/file-browser';
import { UsersPage } from '@/components/users/users-page';
import { RolesPage } from '@/components/roles/roles-page';
import { TrashPage } from '@/components/trash/trash-page';
import { SharedWithMePage } from '@/components/sharing/shared-with-me-page';
import { Loader2, Server, HardDrive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

function Dashboard() {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const [activeFolderId, setActiveFolderId] = useState<string | undefined>();
  const [activeView, setActiveView] = useState<
    'all' | 'trash' | 'shared' | 'users' | 'roles' | 'settings'
  >('all');
  const [shareBrowseContext, setShareBrowseContext] = useState<ShareBrowseContext | null>(null);
  const [openSharedFile, setOpenSharedFile] = useState<{
    fileId: string;
    permissionLevel: 'view' | 'edit';
  } | null>(null);

  const handleOpenSharedFileDone = useCallback(() => setOpenSharedFile(null), []);

  const handleSelectView = useCallback(
    (v: 'all' | 'trash' | 'shared' | 'users' | 'roles' | 'settings') => {
      setShareBrowseContext(null);
      setOpenSharedFile(null);
      setActiveView(v);
    },
    []
  );

  const handleSelectFolder = useCallback((id: string) => {
    setShareBrowseContext(null);
    setOpenSharedFile(null);
    setActiveFolderId(id);
  }, []);

  useEffect(() => {
    if (!isLoading && !user) {
      router.push('/login');
    }
  }, [isLoading, user, router]);

  if (isLoading || !user) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-background">
        <Loader2 className="animate-spin text-primary w-12 h-12 mb-4" />
        <p className="text-lg font-medium text-muted-foreground">Authenticating CoopXL Docs...</p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeView) {
      case 'all':
        return (
          <FileBrowser
            initialFolderId={activeFolderId}
            viewMode="all"
            shareBrowseContext={shareBrowseContext}
            openSharedFile={openSharedFile}
            onOpenSharedFileDone={handleOpenSharedFileDone}
            onExitSharedBrowse={() => handleSelectView('shared')}
          />
        );
      case 'trash':
        return <TrashPage />;
      case 'shared':
        return (
          <SharedWithMePage
            onOpenSharedFolder={(folderId, ctx) => {
              setActiveFolderId(folderId);
              setShareBrowseContext(ctx);
              setActiveView('all');
            }}
            onOpenSharedFile={(fileId, permissionLevel) => {
              setOpenSharedFile({ fileId, permissionLevel });
              setActiveView('all');
            }}
          />
        );
      case 'users':
        return <UsersPage />;
      case 'roles':
        return <RolesPage />;
      case 'settings':
        return (
          <div className="p-8 h-full overflow-auto">
            <div className="max-w-4xl mx-auto space-y-6">
              <div>
                <h1 className="text-2xl font-headline font-bold tracking-tight mb-1">Settings</h1>
                <p className="text-sm text-muted-foreground">
                  Server overview and storage information for your document system.
                </p>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Server className="h-5 w-5 text-primary" />
                      Storage server
                    </CardTitle>
                    <CardDescription>
                      Static overview of current storage capacity and usage.
                    </CardDescription>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    Online
                  </span>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-xl border bg-muted/40 p-4 flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Total capacity</span>
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-4 w-4 text-primary" />
                        <span className="text-lg font-semibold">400 GB</span>
                      </div>
                    </div>
                    <div className="rounded-xl border bg-muted/40 p-4 flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Uploaded files</span>
                      <span className="text-lg font-semibold">30 MB</span>
                      <span className="text-[11px] text-muted-foreground">Total size of all uploaded files</span>
                    </div>
                    <div className="rounded-xl border bg-muted/40 p-4 flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Usage</span>
                      <span className="text-lg font-semibold">&lt; 1%</span>
                      <span className="text-[11px] text-muted-foreground">Plenty of free capacity remaining</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Storage usage</span>
                      <span>30 MB / 400 GB</span>
                    </div>
                    <Progress value={1} className="h-2 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      default:
        return (
          <FileBrowser
            initialFolderId={activeFolderId}
            shareBrowseContext={shareBrowseContext}
            openSharedFile={openSharedFile}
            onOpenSharedFileDone={handleOpenSharedFileDone}
            onExitSharedBrowse={() => handleSelectView('shared')}
          />
        );
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <SidebarNav
        onSelectFolder={handleSelectFolder}
        activeView={activeView}
        onSelectView={handleSelectView}
      />
      <main className="flex-1 overflow-hidden">
        {renderContent()}
      </main>
    </div>
  );
}

export default function RootPage() {
  return <Dashboard />;
}
